/**
 * Pure-data English catalogue for namespace 'model' (W1-03 core-model-ops). Imports only core/util/types.
 *
 *  - `absent.*`: what an ABSENT track means (inspector "Not set: ..." texts, DESIGN.md §6.7); keys referenced by
 *    `PROP_SPECS[prop].absentMessage` and `absentMessageKey(prop, kind, uiMover)` in core/model/props.ts.
 *  - `inv.*`: model invariant violations (core/model/invariants.ts, core/ops/validate.ts); `InvariantViolation.code`
 *    is the full key (e.g. 'model.inv.keyOrder'), `params` its parameters and `message` the English text.
 *  - `shape.problem`: a record field of the wrong type (core/model/schema.ts); `snapshot.*`: why a document snapshot
 *    cannot be read (`DocFormatError`, core/doc/migrate.ts and serialize.ts).
 *  - `txn.rejected`: the reason shown when a transaction breaks an invariant (used by the DocStore).
 *
 * Parameters are language-neutral: ids, numbers, user data (names, paths, value previews) and codes. Every word
 * comes from the text, so codes are spelled out with ICU `select` (table names such as `lines`, property ids such as
 * `moveX`, value types, track owner kinds, shape-problem and asset-path reason codes). Every code has its own branch;
 * `other` only echoes an unknown code. The constants below are shared fragments of those selects; every message is
 * still a plain string once the module is loaded.
 */

import type { MessageCatalog } from '../util/types';

/** A record of table `{p}`, sentence start ("Line 12"). */
const RECORD = (p: string): string =>
  `{${p}, select, bpm {Tempo point} lines {Line} tracks {Track} keys {Keyframe} notes {Note} effects {Effect} videos {Video} markers {Marker} groups {Group} generators {Generator} assets {Asset} other {{${p}}}}`;

/** The records of table `{p}`, mid-sentence ("among the lines"). */
const RECORDS = (p: string): string =>
  `{${p}, select, bpm {tempo points} lines {lines} tracks {tracks} keys {keyframes} notes {notes} effects {effects} videos {videos} markers {markers} groups {groups} generators {generators} assets {assets} other {{${p}}}}`;

/** A line property id (`moveX`...). */
const PROP = (p: string): string =>
  `{${p}, select, moveX {position X} moveY {position Y} rotate {rotation} alpha {opacity} speed {scroll speed} scaleX {scale X} scaleY {scale Y} color {colour} text {text} incline {note tilt} paint {brush} gif {GIF progress} other {{${p}}}}`;

/** A track value type. */
const VALUE_TYPE = (p: string): string =>
  `{${p}, select, number {number} rgb {RGB colour} string {text} vec2 {2D vector} rgba8 {RGBA colour} other {{${p}}}}`;

/** An effect variable type. */
const VAR_TYPE = (p: string): string =>
  `{${p}, select, float {number} vec2 {2D vector} color {RGBA colour} other {{${p}}}}`;

/** A video variable (`alpha` / `dim`). */
const VIDEO_VAR = (p: string): string => `{${p}, select, alpha {opacity} dim {dim} other {{${p}}}}`;

/** A chart-level asset field of ChartMeta. */
const META_FIELD = (p: string): string =>
  `{${p}, select, music {music} illustration {illustration} unlockVideo {unlock video} other {{${p}}}}`;

/**
 * The owner of a track: `ownerKind` (line / effectVar / videoVar), `owner` (line, effect or video id), and `prop` +
 * `layer` (line), `name` (effect variable) or `which` (video variable).
 */
const OWNER = `{ownerKind, select, line {line {owner}, ${PROP('prop')}, layer {layer}} effectVar {effect {owner}, variable "{name}"} videoVar {video {owner}, ${VIDEO_VAR('which')}} other {{ownerKind} {owner}}}`;

/** A record referring to a deleted one: `what` (record kind, or `meta`) and `ref` (its id, or the meta field). */
const REFERRER = `{what, select, note {note {ref}} track {track {ref}} key {keyframe {ref}} line {line {ref}} effect {effect {ref}} video {video {ref}} meta {the chart ${META_FIELD('ref')}} other {{what} {ref}}}`;

/** The asset field of a line, note, effect or video. */
const ASSET_FIELD =
  '{field, select, texture {its texture} hitsound {its hit sound} shader {its shader} asset {its file} other {{field}}}';

/** Why a package path is invalid (`assetPathProblem` codes, core/model/invariants.ts). */
const ASSET_PATH_REASON =
  '{reason, select, empty {the path is empty} leadingSlash {it starts with /} backslash {it uses \\ instead of /} emptyFolder {it has an empty folder name} dotSegment {it contains . or ..} reserved {the name is reserved} clickPng {it ends in click.png} builtinHitsound {it is the name of a built-in hit sound} other {{reason}}}';

/** A package setting (PackageSettings field). */
const PKG_FIELD =
  '{field, select, aspectRatio {aspect ratio} backgroundDim {background dim} lineLength {line length} speedBakeTolPx {speed bake tolerance} rpeVersion {RPE version} other {{field}}}';

/**
 * A field of the wrong shape (`ShapeProblemCode`, core/model/schema.ts). `got` is a language-neutral preview of the
 * value found (`"abc"`, `2.5`, `null`, `[1, 2]`); `min` / `max` / `count` / `values` describe what was expected.
 */
const SHAPE_PROBLEM =
  '{problem, select, number {expected a finite number, got {got}} numberRange {expected a number from {min} to {max}, got {got}} integer {expected an integer, got {got}} integerRange {expected an integer from {min} to {max}, got {got}} string {expected a string, got {got}} boolean {expected true or false, got {got}} oneOf {expected one of {values}, got {got}} array {expected an array, got {got}} tuple {expected a list of {count} items, got {got}} object {expected an object, got {got}} missing {missing} unknownField {unknown field} json {expected a JSON value (finite numbers, plain objects and arrays, no cycles), got {got}} beat {expected a normalised beat (0 <= n < d, gcd(n, d) = 1, d <= 2^32 - 1, i32 whole part)} triple {expected a valid RPE beat triple [a, b, c] (c >= 1, b >= 0)} assetId {expected a non-empty asset id} trackValue {expected a number, a string, or a list of 2 to 4 numbers, got {got}} varConst {expected a number, a vec2 or an RGBA colour, got {got}} other {{problem}, got {got}}}';

const catalog: MessageCatalog = {
  ns: 'model',
  locale: 'en',
  messages: {
    'absent.moveX': 'Not set: position X is 0 (the centre of the screen).',
    'absent.moveY': 'Not set: position Y is 0 (the centre of the screen).',
    'absent.rotate': 'Not set: rotation is 0 degrees.',
    'absent.alpha': 'Not set: the line is invisible (opacity 0), but its notes are still drawn.',
    'absent.alphaUiMover': 'Not set: the attached HUD element is invisible. Add an opacity keyframe to show it.',
    'absent.speed': 'Not set: notes sit on the line and do not move.',
    'absent.scaleX':
      'Not set: Phira draws the full line length. A Length keyframe of 100 % looks the same; a raw value of 1.0 is half as long.',
    'absent.scaleY':
      'Not set: default thickness (6.75 px). Keyed values other than 1.0 are drawn at 0.0076 x value, so the thickness jumps slightly.',
    'absent.scaleX.image': 'Not set: the image is drawn at its pixel size (100 %).',
    'absent.scaleY.image': 'Not set: the image is drawn at its pixel size (100 %).',
    'absent.scaleX.text': 'Not set: text size 100 % (54 px letters).',
    'absent.scaleY.text': 'Not set: text size 100 % (54 px letters).',
    'absent.scaleX.ui': 'Not set: the HUD element keeps its normal size (100 %).',
    'absent.scaleY.ui': 'Not set: the HUD element keeps its normal size (100 %).',
    'absent.scaleX.paint':
      'Not set: the brush keeps its width (100 %). A width keyframe of 100 % looks the same; a raw value of 1.0 is half as wide.',
    'absent.scaleY.paint': 'Not set: the brush keeps its height (100 %).',
    'absent.color':
      'Not set: the line uses the judgement indicator colour (Perfect colour while the play is all Perfect).',
    'absent.color.white': 'Not set: white.',
    'absent.text': 'Not set: this is not a text line.',
    'absent.incline': 'Not set: note tilt is 0 degrees.',
    'absent.paint': 'Not set: this is not a paint line.',
    'absent.gif': 'Not set: a GIF texture shows its first frame only, unless Play automatically is on.',

    'inv.field': `{where}: ${SHAPE_PROBLEM}.`,
    'inv.idKey': `${RECORD('table')} {id} is stored under id {key}.`,
    'inv.idSeq': `The id counter ({idSeq}) must be greater than every id in use (found {id} among the ${RECORDS('table')}).`,
    'inv.idShared': `Id {id} is used both by ${RECORDS('table')} and by ${RECORDS('other')}.`,
    'inv.bpmEmpty': 'The tempo table is empty.',
    'inv.bpmAtZero': 'Exactly one tempo point must be at beat 0 (found {count}).',
    'inv.bpmBeforeZero': 'Tempo point {id} is before beat 0.',
    'inv.bpmDuplicate': 'Tempo points {id} and {other} are both at beat {beat}.',
    'inv.bpmValue': 'Tempo point {id} needs a BPM above 0 (got {bpm}).',
    'inv.orderPermutation':
      '{list, select, lineOrder {The line order must list every line exactly once.} effectOrder {The effect order must list every effect exactly once.} other {{list} must list every record exactly once.}}',
    'inv.lineParentMissing': 'Line {id}: its parent line {parent} does not exist.',
    'inv.lineParentCycle': 'Line {id}: following its parents leads back to the line itself.',
    'inv.lineGroupMissing': 'Line {id}: group {group} does not exist.',
    'inv.assetMissing': `${RECORD('table')} {id}: ${ASSET_FIELD} refers to asset {asset}, which is not in the project.`,
    'inv.assetMissingMeta': `The chart ${META_FIELD('field')} refers to asset {asset}, which is not in the project.`,
    'inv.gifTrackTexture': 'Line {id}: a GIF progress track needs a GIF texture.',
    'inv.gifAutoLoop': 'Line {id}: Play automatically needs a GIF texture and no GIF progress track.',
    'inv.controlOrder':
      'Line {id}: the points of the {control, select, pos {position} alpha {opacity} size {size} y {Y offset} other {{control}}} control must be sorted by distance.',
    'inv.dangling': `${RECORD('table')} {id} was deleted, but ${REFERRER} still refers to it.`,
    'inv.trackEmpty': 'Track {id} has no keyframes.',
    'inv.trackOwnerMissing': `Track {id}: its owner (${OWNER}) does not exist.`,
    'inv.trackOwnerShared': `Tracks {id} and {other} belong to the same property (${OWNER}).`,
    'inv.trackLayer': `Track {id}: ${PROP('prop')} has no layer {layer}.`,
    'inv.trackValueType': `Track {id} holds ${VALUE_TYPE('valueType')} values, but its property needs ${VALUE_TYPE('expected')} values.`,
    'inv.trackVarLink': `Track {id}: ${OWNER} does not use this track.`,
    'inv.trackLeadIn': 'Track {id}: the first keyframe must be at beat 0.',
    'inv.trackImportedLeadIn':
      'Track {id}: it is marked as an imported lead-in, but its first keyframe is not after beat 0.',
    'inv.keyTrackMissing': 'Keyframe {id}: track {track} does not exist.',
    'inv.keyOrder': 'Track {track}: keyframe {id} is not later than the keyframe before it.',
    'inv.keyFirstJump': 'Track {track}: the first keyframe ({id}) cannot jump; it must arrive at the value it leaves.',
    'inv.keyFirstGap': 'Track {track}: the first keyframe ({id}) cannot start a gap.',
    'inv.keyHoldJump': 'Track {track}: keyframe {id} follows a Stay segment, so it must arrive at the held value.',
    'inv.keyLastGifJump': 'Track {track}: the last keyframe of a GIF progress track cannot jump.',
    'inv.keyValue': `Keyframe {id}: the {which, select, vIn {arrive value} vOut {leave value} other {{which}}} is not a valid ${VALUE_TYPE('valueType')} value.`,
    'inv.keyTween': 'Keyframe {id}: easing {easing} must be 1 to 29 with 0 <= left < right <= 1.',
    'inv.keyBezierSpeed': 'Keyframe {id}: scroll speed cannot use a Bezier curve.',
    'inv.keyBezierVar': 'Keyframe {id}: effect and video variables cannot use a Bezier curve.',
    'inv.noteLineMissing': 'Note {id}: line {line} does not exist.',
    'inv.noteHoldEnd': 'Note {id}: a hold cannot end before it starts.',
    'inv.noteEnd':
      'Note {id}: a {kind, select, tap {Tap} drag {Drag} flick {Flick} hold {Hold} other {{kind}}} note must end where it starts.',
    'inv.noteGenMissing': 'Note {id}: generator {gen} does not exist.',
    'inv.effectVarDuplicate': 'Effect {id}: variable "{name}" is defined twice.',
    'inv.effectVarValue': `Effect {id}: the value of "{name}" is not a valid ${VAR_TYPE('type')}.`,
    'inv.varTrack': `{table, select, effects {Effect {id}: variable "{name}"} videos {Video {id}: ${VIDEO_VAR('name')}} other {{table} {id}: {name}}} refers to track {track}, which does not belong to it.`,
    'inv.videoLineMissing': 'Video {id}: line {line} does not exist.',
    'inv.groupOrder': 'Groups {id} and {other} share position {order}.',
    'inv.assetPath': `Asset {id}: "{path}" is not a valid package path (${ASSET_PATH_REASON}).`,
    'inv.assetPathDuplicate': 'Assets {other} and {id} both use the package path "{path}".',
    'inv.previewRange': 'The song preview must start at 0 s or later and end after it starts.',
    'inv.pkgRange': `Package setting ${PKG_FIELD} is out of range ({value}).`,
    'inv.meterOrder': 'Meter marks must be in increasing beat order and have a positive length.',

    'shape.problem': SHAPE_PROBLEM,
    'snapshot.notObject': 'expected an object',
    'snapshot.kind': 'expected "phiedit-doc"',
    'snapshot.version': 'expected a positive integer',
    'snapshot.newer': 'the snapshot has version {version}, made by a newer editor (this one reads up to {supported})',
    'snapshot.noMigration': 'no migration from version {version}',
    'snapshot.duplicateId': 'duplicate id {id} (also at {other})',
    'snapshot.unreadable': 'unreadable',

    'txn.rejected': 'The change was not applied because it would break the chart: {detail}',
  },
};

export default catalog;
